import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { IntegrationStatus } from '@lizrd/contracts';
import { getSyncStatus, listIntegrations } from '../../lib/api';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { SkeletonText } from '../ui/skeleton';
import { RegionsScanned } from '../RegionsScanned';
import { relativeTime } from '../../lib/graph-visuals';

const STATUS_VARIANT: Record<IntegrationStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  CONNECTED: 'success',
  SYNCING: 'success',
  PENDING: 'neutral',
  WARNING: 'warning',
  ERROR: 'danger',
  DISCONNECTED: 'neutral',
};

const GRAPH_STAGE_LABEL: Record<string, string> = {
  IDLE: 'Idle',
  PENDING: 'Queued',
  RUNNING: 'Building knowledge graph…',
  DONE: 'Up to date',
  FAILED: 'Failed',
};

/**
 * Integration + sync health for the domain (DASH-006/008): each connected
 * source with its status and last sync, plus the graph-convergence stage. Errors
 * surface here so the engineer can jump to Settings and fix a broken connection.
 */
export function DomainSyncHealth({ domainId }: { domainId: string }) {
  const integrations = useQuery({
    queryKey: ['integrations', domainId],
    queryFn: () => listIntegrations(domainId),
    refetchInterval: 30_000,
  });
  const sync = useQuery({
    queryKey: ['sync-status', domainId],
    queryFn: () => getSyncStatus(domainId),
    refetchInterval: 15_000,
  });

  const items = integrations.data ?? [];
  const stage = sync.data?.graph.stage;
  const regionsById = new Map(
    (sync.data?.integrations ?? []).map((s) => [s.integrationId, s.regions]),
  );

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
          Connections &amp; sync
        </h2>
        <Link
          to="../integrations"
          className="text-xs font-semibold text-iris hover:text-iris-strong"
        >
          Manage →
        </Link>
      </div>

      {!integrations.data ? (
        <SkeletonText lines={3} lineClassName="h-4" />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted">
          No sources connected.{' '}
          <Link to="../integrations" className="text-iris hover:text-iris-strong">
            Connect one
          </Link>{' '}
          to start syncing.
        </p>
      ) : (
        <ul className="space-y-2 text-sm">
          {items.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-bone">{i.displayName}</div>
                <div className="text-[0.7rem] text-muted">
                  {i.kind === 'CLOUD' ? 'Cloud' : 'Code'} · last sync {relativeTime(i.lastSyncedAt)}
                  <RegionsScanned regions={regionsById.get(i.id)} className="ml-1" />
                </div>
              </div>
              <Badge variant={STATUS_VARIANT[i.status] ?? 'neutral'} dot>
                {i.status.toLowerCase()}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {stage && stage !== 'IDLE' && (
        <p className="mt-4 border-t border-hairline pt-3 text-xs text-haze">
          Knowledge graph: <span className="text-bone">{GRAPH_STAGE_LABEL[stage] ?? stage}</span>
        </p>
      )}
    </Card>
  );
}
